import React, { useState, KeyboardEvent } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  deleteReading,
  updateReading,
} from "@features/workspace-readings/state/workspaceReadingsSlice";
import {
  closeResourceManagementModal,
  setBaseView,
} from "@store/slices/viewSlice";
import { selectReading } from "@store/thunks/workspaceThunks";
import { selectIsEvaluator } from "@store/selectors";
import { WorkspaceReadingView } from "@/types/models";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

interface WorkspaceReadingsTableProps {
  readings: WorkspaceReadingView[];
}

export const WorkspaceReadingsTable: React.FC<WorkspaceReadingsTableProps> = ({
  readings,
}) => {
  const dispatch = useAppDispatch();
  const isEvaluator = useAppSelector(selectIsEvaluator);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);

  const handleDelete = async (readingId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this reading? This action cannot be undone."
      )
    ) {
      return;
    }

    setDeletingId(readingId);
    try {
      await dispatch(deleteReading(readingId)).unwrap();
    } catch (err) {
      console.error("Failed to delete reading:", err);
      alert("Failed to delete reading. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleView = (readingId: string) => {
    dispatch(setBaseView("reading"));
    dispatch(selectReading(readingId));
    dispatch(closeResourceManagementModal());
  };

  const startEdit = (readingId: string, currentName: string) => {
    setEditingId(readingId);
    setEditValue(currentName);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const commitEdit = async (readingId: string) => {
    const next = editValue.trim();
    const current = readings.find((r) => r.reading_id === readingId);
    if (!current || !next || next === current.reading_name) {
      cancelEdit();
      return;
    }
    setSavingId(readingId);
    try {
      await dispatch(
        updateReading({ readingId, data: { reading_name: next } })
      ).unwrap();
    } catch (err) {
      console.error("Failed to rename reading:", err);
      alert("Failed to rename reading. Please try again.");
    } finally {
      setSavingId(null);
      cancelEdit();
    }
  };

  const handleEditKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    readingId: string
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit(readingId);
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>Created</TableCell>
            {isEvaluator && (
              <>
                <TableCell>Assigned By</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Required</TableCell>
              </>
            )}
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {readings.map((reading) => {
            const isEditing = editingId === reading.reading_id;
            const isSaving = savingId === reading.reading_id;
            const isDeleting = deletingId === reading.reading_id;
            return (
              <TableRow key={reading.reading_id} hover>
                <TableCell>
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleEditKeyDown(e, reading.reading_id)}
                      onBlur={() => commitEdit(reading.reading_id)}
                      autoFocus
                      disabled={isSaving}
                      inputProps={{ maxLength: 255 }}
                      fullWidth
                    />
                  ) : (
                    <>
                      <Typography variant="body1" fontWeight={500}>
                        {reading.reading_name}
                      </Typography>
                      {reading.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {reading.description}
                        </Typography>
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(reading.created_at)}
                  </Typography>
                </TableCell>
                {isEvaluator && (
                  <>
                    <TableCell>
                      {reading.assigned_by_username || "Unknown"}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={
                          reading.due_date &&
                          new Date(reading.due_date) < new Date()
                            ? "error"
                            : "text.primary"
                        }
                      >
                        {formatDate(reading.due_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={reading.required ? "Required" : "Optional"}
                        size="small"
                        color={reading.required ? "error" : "default"}
                        variant={reading.required ? "filled" : "outlined"}
                      />
                    </TableCell>
                  </>
                )}
                <TableCell align="right">
                  <Box display="flex" gap={1} justifyContent="flex-end">
                    <Tooltip title="Open reading">
                      <span>
                        <IconButton
                          size="small"
                          color="inherit"
                          onClick={() => handleView(reading.reading_id)}
                          disabled={isEditing}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Rename">
                      <span>
                        <IconButton
                          size="small"
                          color="inherit"
                          onClick={() =>
                            startEdit(reading.reading_id, reading.reading_name)
                          }
                          disabled={isEditing}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(reading.reading_id)}
                          color="error"
                          disabled={isDeleting || isEditing}
                        >
                          {isDeleting ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
